import { SEO } from '../components/SEO';
import { ContactForm } from '../components/ContactForm';
import { companyInfo } from '../data/content';
import { Phone, Mail, Clock, MapPin } from 'lucide-react';

export function Kontakt() {
  return (
    <>
      <SEO 
        title="Kontakt"
        description="Kontaktieren Sie ConciergeUnit für Anfragen zu Night-Auditor-Service, Receptionsservice oder Event-Support."
        path="/kontakt"
      />

      {/* Header Section */}
      <section className="pt-32 pb-20 bg-brand-950">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-platin-400 font-semibold text-sm uppercase tracking-widest">
              Kontakt
            </span>
            <h1 className="heading-xl text-white mt-4 mb-6">
              Wir freuen uns auf Ihre Nachricht
            </h1>
            <p className="text-xl text-platin-200 leading-relaxed">
              Haben Sie Fragen zu unseren Leistungen oder möchten Sie ein individuelles 
              Angebot erhalten? Kontaktieren Sie uns – wir melden uns schnellstmöglich bei Ihnen.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-brand-950 mb-6">
                  Direkter Kontakt
                </h2>
                <p className="text-gray-600 mb-8">
                  Erreichen Sie uns direkt per Telefon oder E-Mail. 
                  Für dringende Anfragen empfehlen wir einen Telefonanruf.
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start gap-4 p-5 rounded-xl border border-platin-200 hover:border-platin-400 hover:shadow-soft transition-all group"
                >
                  <div className="w-12 h-12 bg-brand-950 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-platin-200 transition-colors">
                    <Phone className="w-5 h-5 text-platin-200 group-hover:text-brand-950 transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-950">Telefon</p>
                    <p className="text-brand-600">{companyInfo.phone}</p>
                    <p className="text-sm text-gray-500 mt-1">Mo-Fr, 09:00 - 18:00 Uhr</p>
                  </div>
                </a>

                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-start gap-4 p-5 rounded-xl border border-platin-200 hover:border-platin-400 hover:shadow-soft transition-all group"
                >
                  <div className="w-12 h-12 bg-brand-950 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-platin-200 transition-colors">
                    <Mail className="w-5 h-5 text-platin-200 group-hover:text-brand-950 transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-950">E-Mail</p>
                    <p className="text-brand-600">{companyInfo.email}</p>
                    <p className="text-sm text-gray-500 mt-1">Antwort innerhalb 24h</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-5 rounded-xl border border-platin-200">
                  <div className="w-12 h-12 bg-platin-100 rounded-xl flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-brand-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-950">Verfügbarkeit</p>
                    <p className="text-gray-600">Night-Audit: 24/7</p>
                    <p className="text-sm text-gray-500 mt-1">Events: Nach Vereinbarung</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl border border-platin-200">
                  <div className="w-12 h-12 bg-platin-100 rounded-xl flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-brand-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-950">Einsatzgebiet</p>
                    <p className="text-gray-600">Deutschlandweit</p>
                    <p className="text-sm text-gray-500 mt-1">Hauptsitz: Düsseldorf</p>
                  </div>
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="bg-brand-50 border-l-4 border-brand-950 rounded-r-xl p-6">
                <h3 className="font-semibold text-brand-950 mb-2">
                  Schnelle Antwort garantiert
                </h3>
                <p className="text-brand-700 text-sm">
                  Wir bemühen uns, alle Anfragen innerhalb von 24 Stunden zu beantworten. 
                  Bei dringenden Angelegenheiten erreichen Sie uns telefonisch.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-platin-50 rounded-2xl p-8 md:p-12 border border-platin-200">
                <h2 className="text-2xl font-bold text-brand-950 mb-2">
                  Kontaktformular
                </h2>
                <p className="text-gray-600 mb-8">
                  Füllen Sie das Formular aus und wir melden uns umgehend bei Ihnen.
                </p>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="section-padding bg-platin-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-md text-brand-950 mb-8 text-center">
              Häufig gestellte Fragen
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl border border-platin-200">
                <h3 className="font-semibold text-brand-950 mb-2">
                  Wie kurzfristig können Sie eingesetzt werden?
                </h3>
                <p className="text-gray-600 text-sm">
                  Je nach Verfügbarkeit sind wir oft bereits innerhalb von 24-48 Stunden einsatzbereit.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-platin-200">
                <h3 className="font-semibold text-brand-950 mb-2">
                  In welchen Hotelsystemen sind Sie erfahren?
                </h3>
                <p className="text-gray-600 text-sm">
                  Wir arbeiten mit Opera, SIHOT, Protel und vielen weiteren Systemen.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-platin-200">
                <h3 className="font-semibold text-brand-950 mb-2">
                  Wie werden die Kosten berechnet?
                </h3>
                <p className="text-gray-600 text-sm">
                  Die Kosten richten sich nach Art des Einsatzes. Gerne erstellen wir Ihnen ein individuelles Angebot.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-platin-200">
                <h3 className="font-semibold text-brand-950 mb-2">
                  Sind Sie versichert?
                </h3>
                <p className="text-gray-600 text-sm">
                  Ja, wir verfügen über eine entsprechende Betriebshaftpflichtversicherung.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
